import { memo } from 'react';
import TaskNode from '../../TaskNode';
import DecisionNode from '../../TaskNode/DecisionNode';
import MilestoneNode from '../../TaskNode/MilestoneNode';
import NoteNode from '../../TaskNode/NoteNode';

/**
 * MemoizedTaskNode - Prevents unnecessary re-renders of individual nodes
 * PERFORMANCE: Zoom is intentionally NOT compared here - zoom changes are handled
 * via CSS transforms and useLayoutEffect in FlowchartCanvas, not React re-renders.
 * The quick view handlers are also skipped since they're conditionally passed
 * based on zoom level but don't affect the node's visual appearance.
 */
export const MemoizedTaskNode = memo(TaskNode, (prevProps, nextProps) => {
  if (prevProps.layoutSurfaceEpoch !== nextProps.layoutSurfaceEpoch) return false;
  if (prevProps.task !== nextProps.task) return false;
  if (prevProps.position?.x !== nextProps.position?.x || prevProps.position?.y !== nextProps.position?.y) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isDragging !== nextProps.isDragging) return false;
  if (prevProps.isConnecting !== nextProps.isConnecting) return false;
  if (prevProps.isConnectionTarget !== nextProps.isConnectionTarget) return false;
  if (prevProps.isConnectionHovered !== nextProps.isConnectionHovered) return false;
  if (prevProps.isFocusedMain !== nextProps.isFocusedMain) return false;
  if (prevProps.isFocusDependency !== nextProps.isFocusDependency) return false;
  if (prevProps.isAnimating !== nextProps.isAnimating) return false;
  if (prevProps.isFadingOut !== nextProps.isFadingOut) return false;
  if (prevProps.isFadingIn !== nextProps.isFadingIn) return false;
  if (prevProps.isDropTarget !== nextProps.isDropTarget) return false;
  if (prevProps.isHoveredFromHUD !== nextProps.isHoveredFromHUD) return false;
  if (prevProps.lockState !== nextProps.lockState) return false;
  if (prevProps.nestedSprint !== nextProps.nestedSprint) return false;
  if (prevProps.nestedSprints !== nextProps.nestedSprints) return false;
  if (prevProps.allSprints !== nextProps.allSprints) return false;
  if (prevProps.cardDisplaySettings !== nextProps.cardDisplaySettings) return false;
  if (prevProps.isMultiCampaignMode !== nextProps.isMultiCampaignMode) return false;
  return true;
});

/**
 * MemoizedDecisionNode - Prevents unnecessary re-renders of decision nodes
 * PERFORMANCE: Zoom is intentionally NOT compared - see MemoizedTaskNode comment
 */
export const MemoizedDecisionNode = memo(DecisionNode, (prevProps, nextProps) => {
  if (prevProps.layoutSurfaceEpoch !== nextProps.layoutSurfaceEpoch) return false;
  if (prevProps.task !== nextProps.task) return false;
  if (prevProps.position?.x !== nextProps.position?.x || prevProps.position?.y !== nextProps.position?.y) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isDragging !== nextProps.isDragging) return false;
  if (prevProps.isConnecting !== nextProps.isConnecting) return false;
  if (prevProps.isConnectionTarget !== nextProps.isConnectionTarget) return false;
  if (prevProps.isConnectionHovered !== nextProps.isConnectionHovered) return false;
  if (prevProps.isFocusedMain !== nextProps.isFocusedMain) return false;
  if (prevProps.isFocusDependency !== nextProps.isFocusDependency) return false;
  if (prevProps.isAnimating !== nextProps.isAnimating) return false;
  if (prevProps.isFadingOut !== nextProps.isFadingOut) return false;
  if (prevProps.isFadingIn !== nextProps.isFadingIn) return false;
  if (prevProps.isDropTarget !== nextProps.isDropTarget) return false;
  if (prevProps.isHoveredFromHUD !== nextProps.isHoveredFromHUD) return false;
  if (prevProps.lockState !== nextProps.lockState) return false;
  if (prevProps.connectingBranch !== nextProps.connectingBranch) return false;
  if (prevProps.cardDisplaySettings !== nextProps.cardDisplaySettings) return false;
  if (prevProps.isMultiCampaignMode !== nextProps.isMultiCampaignMode) return false;
  return true;
});

export const MemoizedNoteNode = memo(NoteNode, (prevProps, nextProps) => {
  if (prevProps.layoutSurfaceEpoch !== nextProps.layoutSurfaceEpoch) return false;
  if (prevProps.task !== nextProps.task) return false;
  if (prevProps.position?.x !== nextProps.position?.x || prevProps.position?.y !== nextProps.position?.y) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isDragging !== nextProps.isDragging) return false;
  if (prevProps.isAnimating !== nextProps.isAnimating) return false;
  if (prevProps.isHoveredFromHUD !== nextProps.isHoveredFromHUD) return false;
  return true;
});

export const MemoizedMilestoneNode = memo(MilestoneNode, (prevProps, nextProps) => {
  if (prevProps.layoutSurfaceEpoch !== nextProps.layoutSurfaceEpoch) return false;
  if (prevProps.task !== nextProps.task) return false;
  if (prevProps.position?.x !== nextProps.position?.x || prevProps.position?.y !== nextProps.position?.y) return false;
  if (prevProps.hostPosition?.x !== nextProps.hostPosition?.x || prevProps.hostPosition?.y !== nextProps.hostPosition?.y) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isDragging !== nextProps.isDragging) return false;
  if (prevProps.isAnimating !== nextProps.isAnimating) return false;
  if (prevProps.isHoveredFromHUD !== nextProps.isHoveredFromHUD) return false;
  if (prevProps.allTasks !== nextProps.allTasks) return false;
  if (prevProps.nestedSprints !== nextProps.nestedSprints) return false;
  if (prevProps.allSprints !== nextProps.allSprints) return false;
  if (prevProps.statuses !== nextProps.statuses) return false;
  return true;
});
